/**
 * Procedural hover SFX for the map HUD.
 *
 * Hover fires constantly while the pointer travels, so these are synthesised
 * on the fly with Web Audio instead of shipped as .sfxc files: every voice is
 * a couple of oscillators plus a noise burst, so it costs nothing to load,
 * stays under ~200ms, and can be re-pitched per element — sweeping across the
 * bottom bar plays a rising pentatonic run rather than the same click five
 * times.
 *
 * Voices are deliberately quiet (see VOICE_GAIN); they sit under the click
 * sounds in `soundUtils.ts`, they don't compete with them.
 */

export type HoverVoice =
  /** Airy glass swell, pitched per building — the map itself. */
  | "building"
  /** Bright silver tick — top bar gear / bell / rank. */
  | "topbar"
  /** Dull plank knock, pitched per slot — bottom bar nav. */
  | "bottombar"
  /** Rising fifth + sparkle — the centre chest / play button. */
  | "chest"
  /** Parchment rustle — the chat preview strip. */
  | "chat"
  /** Coin shimmer — the resource bar. */
  | "resource";

/** Headroom per voice. Hover sound is ambience: it should never announce itself. */
const VOICE_GAIN: Record<HoverVoice, number> = {
  building: 0.11,
  topbar: 0.1,
  bottombar: 0.13,
  chest: 0.14,
  chat: 0.09,
  resource: 0.08,
};

/**
 * Minimum gap between two plays of the same voice, ms. The building swell has
 * a ~200ms tail, so it gets a longer gate — a fast pan across the village would
 * otherwise stack four notes into a chord.
 */
const VOICE_THROTTLE_MS: Partial<Record<HoverVoice, number>> = { building: 120 };
const DEFAULT_THROTTLE_MS = 55;
/** Minimum gap between any two hover sounds — stops fast sweeps turning to mush. */
const GLOBAL_THROTTLE_MS = 35;

let audioCtx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
let unlockBound = false;

const lastPlayedAt: Partial<Record<HoverVoice, number>> = {};
let lastAnyPlayedAt = 0;

const getCtx = (): AudioContext | null => {
  if (audioCtx) return audioCtx;
  if (typeof window === "undefined") return null;

  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;

  try {
    audioCtx = new Ctor();
    master = audioCtx.createGain();
    master.gain.value = 1;
    master.connect(audioCtx.destination);
  } catch {
    return null;
  }
  return audioCtx;
};

/**
 * Autoplay policy: a context created before the first gesture starts
 * suspended. Resume on the next gesture so the very first hover after a page
 * load isn't silently swallowed.
 */
const bindUnlock = () => {
  if (unlockBound || typeof window === "undefined") return;
  unlockBound = true;

  const resume = () => {
    audioCtx?.resume().catch(() => {});
    window.removeEventListener("pointerdown", resume);
    window.removeEventListener("keydown", resume);
    window.removeEventListener("touchstart", resume);
  };

  window.addEventListener("pointerdown", resume, { passive: true });
  window.addEventListener("keydown", resume);
  window.addEventListener("touchstart", resume, { passive: true });
};

/** 0.5s of white noise, reused by every percussive voice. */
const getNoise = (ctx: AudioContext): AudioBuffer => {
  if (noiseBuffer) return noiseBuffer;
  const length = Math.floor(ctx.sampleRate * 0.5);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
};

const MIN_GAIN = 0.0001;

/** Percussive attack/decay envelope on a fresh gain node. */
const shapedGain = (
  ctx: AudioContext,
  at: number,
  peak: number,
  attack: number,
  decay: number
): GainNode => {
  const gain = ctx.createGain();
  const g = gain.gain;
  g.setValueAtTime(MIN_GAIN, at);
  g.exponentialRampToValueAtTime(Math.max(peak, MIN_GAIN * 2), at + attack);
  g.exponentialRampToValueAtTime(MIN_GAIN, at + attack + decay);
  return gain;
};

type ToneOptions = {
  freq: number;
  /** Optional glide target — a small downward slide reads as "struck". */
  endFreq?: number;
  at: number;
  peak: number;
  attack?: number;
  decay: number;
  type?: OscillatorType;
};

const tone = (ctx: AudioContext, dest: AudioNode, o: ToneOptions) => {
  const attack = o.attack ?? 0.004;
  const osc = ctx.createOscillator();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(o.freq, o.at);
  if (o.endFreq && o.endFreq !== o.freq) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(o.endFreq, 1),
      o.at + attack + o.decay
    );
  }

  const gain = shapedGain(ctx, o.at, o.peak, attack, o.decay);
  osc.connect(gain).connect(dest);

  const end = o.at + attack + o.decay + 0.02;
  osc.start(o.at);
  osc.stop(end);
  osc.onended = () => {
    try {
      osc.disconnect();
      gain.disconnect();
    } catch {
      /* already torn down */
    }
  };
};

type NoiseOptions = {
  at: number;
  peak: number;
  decay: number;
  attack?: number;
  filter?: BiquadFilterType;
  freq?: number;
  q?: number;
  /** Filter sweep target — gives the burst a sense of movement. */
  endFreq?: number;
};

const noise = (ctx: AudioContext, dest: AudioNode, o: NoiseOptions) => {
  const attack = o.attack ?? 0.002;
  const source = ctx.createBufferSource();
  source.buffer = getNoise(ctx);
  // Offset the read head so repeated bursts aren't sample-identical.
  const offset = Math.random() * 0.3;

  const gain = shapedGain(ctx, o.at, o.peak, attack, o.decay);
  const end = o.at + attack + o.decay + 0.02;

  let head: AudioNode = source;
  let filter: BiquadFilterNode | null = null;
  if (o.filter) {
    filter = ctx.createBiquadFilter();
    filter.type = o.filter;
    filter.frequency.setValueAtTime(o.freq ?? 1000, o.at);
    if (o.endFreq) {
      filter.frequency.exponentialRampToValueAtTime(
        Math.max(o.endFreq, 20),
        end
      );
    }
    if (o.q != null) filter.Q.value = o.q;
    source.connect(filter);
    head = filter;
  }
  head.connect(gain).connect(dest);

  source.start(o.at, offset, attack + o.decay + 0.02);
  source.stop(end);
  source.onended = () => {
    try {
      source.disconnect();
      filter?.disconnect();
      gain.disconnect();
    } catch {
      /* already torn down */
    }
  };
};

/** Minor pentatonic, in semitones — any two notes sound intentional together. */
const PENTATONIC = [0, 3, 5, 7, 10];
const semitone = (base: number, steps: number) => base * Math.pow(2, steps / 12);

/**
 * Stable per-element pitch. `seed` is a building id or a bar slot index, so the
 * same element always answers with the same note.
 */
const seededStep = (seed: number) =>
  PENTATONIC[Math.abs(Math.round(seed)) % PENTATONIC.length];

const renderVoice = (
  ctx: AudioContext,
  dest: AudioNode,
  voice: HoverVoice,
  seed: number
) => {
  const now = ctx.currentTime;
  // A few ms of lead time — scheduling exactly at currentTime can clip the attack.
  const at = now + 0.005;

  switch (voice) {
    case "building": {
      // Highlight, not impact. Hovering a roof happens dozens of times while
      // panning the map, so this is a soft glass swell in the upper register:
      // eased attack (no percussive front edge), no low body, and an airy
      // filter sweep *upward* so it reads as focus landing on the building
      // rather than something being struck.
      const freq = semitone(587.33, seededStep(seed));
      tone(ctx, dest, {
        freq,
        at,
        peak: 0.5,
        attack: 0.018,
        decay: 0.2,
        type: "sine",
      });
      // Octave partial only — a bell's inharmonics would ring against the
      // next building's note during a sweep.
      tone(ctx, dest, {
        freq: freq * 2,
        at: at + 0.01,
        peak: 0.12,
        attack: 0.02,
        decay: 0.12,
        type: "sine",
      });
      noise(ctx, dest, {
        at,
        peak: 0.1,
        attack: 0.02,
        decay: 0.09,
        filter: "bandpass",
        freq: 1600,
        endFreq: 3600,
        q: 1.2,
      });
      break;
    }

    case "topbar": {
      // Silver tick: two detuned highs, a hair of air on top.
      tone(ctx, dest, { freq: 1568, at, peak: 0.55, decay: 0.05, type: "triangle" });
      tone(ctx, dest, {
        freq: 2349,
        at: at + 0.006,
        peak: 0.28,
        decay: 0.045,
        type: "triangle",
      });
      noise(ctx, dest, {
        at,
        peak: 0.14,
        decay: 0.025,
        filter: "highpass",
        freq: 4200,
      });
      break;
    }

    case "bottombar": {
      // Plank knock, pitched by slot index so a left-to-right sweep climbs.
      const freq = semitone(147, seededStep(seed));
      tone(ctx, dest, {
        freq,
        endFreq: freq * 0.82,
        at,
        peak: 0.85,
        decay: 0.1,
        type: "sine",
      });
      noise(ctx, dest, {
        at,
        peak: 0.5,
        decay: 0.045,
        filter: "lowpass",
        freq: 900,
        endFreq: 300,
      });
      break;
    }

    case "chest": {
      // Rising fifth + sparkle — the one voice allowed to sound like a reward.
      tone(ctx, dest, { freq: 523.25, at, peak: 0.6, decay: 0.14, type: "triangle" });
      tone(ctx, dest, {
        freq: 783.99,
        at: at + 0.055,
        peak: 0.5,
        decay: 0.18,
        type: "triangle",
      });
      tone(ctx, dest, {
        freq: 1567.98,
        at: at + 0.09,
        peak: 0.16,
        decay: 0.16,
        type: "sine",
      });
      break;
    }

    case "chat": {
      // Parchment: two staggered bandpassed puffs, no pitch at all.
      noise(ctx, dest, {
        at,
        peak: 0.55,
        decay: 0.06,
        filter: "bandpass",
        freq: 2600,
        endFreq: 1700,
        q: 0.8,
      });
      noise(ctx, dest, {
        at: at + 0.045,
        peak: 0.3,
        decay: 0.07,
        filter: "bandpass",
        freq: 1900,
        endFreq: 3000,
        q: 0.9,
      });
      break;
    }

    case "resource": {
      // Coin shimmer: a little arpeggio up the top of the scale.
      [2093, 2637.02, 3135.96].forEach((freq, i) => {
        tone(ctx, dest, {
          freq,
          at: at + i * 0.028,
          peak: 0.4 - i * 0.1,
          decay: 0.06,
          type: "sine",
        });
      });
      break;
    }
  }
};

export type HoverSfxOptions = {
  /** 0..1 — normally derived from userSettings.soundEffects. 0 mutes. */
  volume?: number;
  /** Stable id (building id, bar slot) used to pick this element's note. */
  seed?: number;
};

/** Fire a hover voice. Cheap, throttled, and a no-op when muted or unsupported. */
export const playHoverSfx = (
  voice: HoverVoice,
  { volume = 0.5, seed = 0 }: HoverSfxOptions = {}
): void => {
  if (volume <= 0) return;

  const now = typeof performance !== "undefined" ? performance.now() : Date.now();
  if (now - lastAnyPlayedAt < GLOBAL_THROTTLE_MS) return;
  const gate = VOICE_THROTTLE_MS[voice] ?? DEFAULT_THROTTLE_MS;
  if (now - (lastPlayedAt[voice] ?? 0) < gate) return;

  const ctx = getCtx();
  if (!ctx || !master) return;

  bindUnlock();
  if (ctx.state === "suspended") {
    // No gesture yet — the unlock listener will pick it up; skip silently.
    ctx.resume().catch(() => {});
    return;
  }

  lastAnyPlayedAt = now;
  lastPlayedAt[voice] = now;

  const bus = ctx.createGain();
  bus.gain.value = VOICE_GAIN[voice] * Math.min(1, Math.max(0, volume));
  bus.connect(master);

  renderVoice(ctx, bus, voice, seed);

  // Voices are all well under 400ms; drop the bus once they've rung out.
  window.setTimeout(() => {
    try {
      bus.disconnect();
    } catch {
      /* already torn down */
    }
  }, 600);
};

/** `soundEffects` is a 0..10 slider in settings; hover wants a 0..1 gain. */
export const sfxVolumeFromSettings = (userSettings: any): number => {
  const raw = Number(userSettings?.soundEffects);
  if (!Number.isFinite(raw)) return 0.5;
  return Math.min(1, Math.max(0, raw / 10));
};
